import { type HTMLAttributes } from 'react'
import { motion } from 'motion/react'
import { Calendar, Clock, Tag, Repeat, MapPin, Globe } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { formatPrice } from '@/shared/lib/format'
import { OptimizedImage } from '@/shared/ui'
import type { Event } from '../model/types'
import { 
  getEventDate, 
  getEventTime, 
  getRecurrenceLabel, 
  isRecurringEvent,
  hasEventDate 
} from '../lib/helpers'

export interface EventCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Событие для отображения
   */
  event: Event
  /**
   * Обработчик клика на карточку
   */
  onClick?: () => void
  /**
   * Состояние загрузки
   * @default false
   */
  loading?: boolean
  /**
   * Callback для haptic feedback
   */
  onHapticFeedback?: (type?: 'light' | 'medium') => void
}

/**
 * UI компонент: Карточка события
 * 
 * Поддерживает:
 * - Повторяющиеся события (еженедельно, ежемесячно и т.д.)
 * - События без даты
 * - Онлайн события
 */
export function EventCard({
  event,
  onClick,
  loading = false,
  onHapticFeedback,
  className,
  ...props
}: EventCardProps) {
  const handleClick = () => {
    if (loading) return
    onHapticFeedback?.('light')
    onClick?.()
  }

  // Получаем изображение
  const imageUrl = event.imageUrl || event.images?.[0] || undefined
  
  // Умное форматирование даты (с учётом повторяемости)
  const eventDate = getEventDate(event)
  const eventTime = getEventTime(event)
  const recurrenceLabel = getRecurrenceLabel(event.recurrenceType)
  const isRecurring = isRecurringEvent(event)
  const hasDate = hasEventDate(event)
  
  // Форматируем цену
  const priceText = event.isFree 
    ? 'Бесплатно' 
    : event.price 
      ? formatPrice(event.price) 
      : 'Уточняется'

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className={cn(
        'bg-white dark:bg-dark-card rounded-[20px] overflow-hidden',
        'shadow-[0_2px_8px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)]',
        'border border-gray-100 dark:border-dark-border',
        'cursor-pointer transition-all duration-300',
        'hover:shadow-[0_4px_16px_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.4),0_4px_8px_rgba(0,0,0,0.3)]',
        loading && 'opacity-50 pointer-events-none',
        className
      )}
      onClick={handleClick}
      {...(props as any)}
    >
      {/* Изображение события */}
      {imageUrl && (
        <div className="relative w-full aspect-[16/10] bg-gray-100 dark:bg-gray-800/50 overflow-hidden">
          <OptimizedImage
            src={imageUrl}
            alt={event.title}
            preset="card"
            wrapperClassName="h-full w-full"
            className="object-cover"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
          
          {/* Бейджи сверху */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
            {/* Повторяющееся событие */}
            {isRecurring && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                <Repeat className="w-3 h-3" />
                {recurrenceLabel}
              </span>
            )}
            
            {/* Бесплатно */}
            {event.isFree && (
              <span className="ml-auto px-2.5 py-1 bg-emerald-500/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                Бесплатно
              </span>
            )}
          </div>
        </div>
      )}

      {/* Контент карточки */}
      <div className="p-4 space-y-3">
        {/* Заголовок */}
        <h3 className="text-lg font-semibold line-clamp-2 text-ink dark:text-white leading-tight">
          {event.title}
        </h3>

        {/* Мета-информация */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
          {/* Категория */}
          {event.category && (
            <span className="flex items-center gap-1.5">
              <Tag className="w-4 h-4 text-primary/70 dark:text-primary-light/70" />
              {event.category.name}
            </span>
          )}

          {/* Дата и время */}
          {hasDate && (
            <span className="flex items-center gap-1.5">
              {isRecurring ? (
                <Repeat className="w-4 h-4 text-purple-500" />
              ) : (
                <Calendar className="w-4 h-4 text-primary/70 dark:text-primary-light/70" />
              )}
              <span>{eventDate}</span>
              {eventTime && <span className="text-gray-400 dark:text-gray-500">· {eventTime}</span>}
            </span>
          )}

          {/* Место или онлайн */}
          {event.isOnline ? (
            <span className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
              <Globe className="w-4 h-4" />
              Онлайн
            </span>
          ) : event.location ? (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-primary/70 dark:text-primary-light/70" />
              {event.location}
            </span>
          ) : null}

          {/* Длительность */}
          {event.durationMinutes && (
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-primary/70 dark:text-primary-light/70" />
              {event.durationMinutes} мин
            </span>
          )}
        </div>

        {/* Цена (только если не бесплатно, т.к. бесплатно показываем бейджем) */}
        {!event.isFree && (
          <div className="flex items-center pt-3 border-t border-gray-100 dark:border-dark-border">
            <span className="text-xl font-bold text-primary dark:text-primary-light">
              {priceText}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  )
}
